/**
 * Turns server events into cache invalidations.
 *
 * The stream says only *that* something changed, so the response is always the
 * same shape: mark the affected queries stale and let React Query refetch them
 * through the ordinary, authorised path. That keeps one source of truth for
 * what a user is allowed to see.
 */

import { useEffect } from "react";
import { whilePageVisible } from "./pageActivity.js";
import { useQueryClient } from "@tanstack/react-query";
import { connectToGroupEvents, type LiveEvent } from "./liveEvents.js";

export function useGroupEvents(
  groupId: string | undefined,
  enabled = true,
): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!groupId || !enabled) return undefined;

    const handle = (event: LiveEvent) => {
      switch (event.type) {
        case "post.created":
        case "post.deleted":
          void queryClient.invalidateQueries({ queryKey: ["wall", groupId] });
          break;

        case "comment.created":
        case "comment.deleted": {
          // The feed owns the comment count, so it goes stale too.
          void queryClient.invalidateQueries({ queryKey: ["wall", groupId] });
          const postId = event.postId;
          if (typeof postId === "string") {
            void queryClient.invalidateQueries({
              queryKey: ["wall-comments", groupId, postId],
            });
          }
          const fileId = event.fileId;
          if (typeof fileId === "string") {
            void queryClient.invalidateQueries({
              queryKey: ["photo-comments", groupId, fileId],
            });
          }
          break;
        }

        case "reaction.changed":
          /*
           * Only the feed, and only for post reactions. Invalidating on our own
           * reaction would undo the optimistic update we just applied — but the
           * event fans out to everyone including the person who caused it, and
           * the refetch settles on the same answer either way.
           */
          if (event.targetType === "post") {
            void queryClient.invalidateQueries({ queryKey: ["wall", groupId] });
          }
          break;

        case "file.ready":
          void queryClient.invalidateQueries({
            queryKey: ["gallery", groupId],
          });
          break;

        default:
          break;
      }
    };

    let connectedBefore = document.hidden;
    return whilePageVisible(() => {
      if (connectedBefore) {
        // Events were missed while hidden. Refresh this group's visible queries.
        void queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes(groupId) });
      }
      connectedBefore = true;
      const connection = connectToGroupEvents(groupId, handle);
      return () => connection.close();
    });
  }, [groupId, enabled, queryClient]);
}
